// #region imports
import imageCompression from "browser-image-compression";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import { toast } from "react-toastify";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import swal from "sweetalert";
import {
  CheckPermission,
  convertToDateString,
  DateOfBirthValidater,
  isUrdu,
  JoiningDateValidater,
  LoadingSpinner,
} from "../../constants/const";
import { encryptStorage } from "../../constants/EncryptStorage";
import { GetLanguageString, NoResult } from "../helper/Components";
import { GetSelectedLanguage } from "../helper/Method";
import { addMember } from "../member/ApiMember";
import SelectMemberUnit from "../member/SelectMemberUnit";
import { ApiNotifications } from "../notification/ApiNotifications";
import TableView from "../shared/SimilarRecordTable";
import useForm from "../shared/useForm";

// #endregion imports

// #region DataFunctionality

const ConvertHabibToRafiq = ({ member, setHabibMemberModal, memberUnitId }) => {
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));

  const memberValues = {
    unit: {
      value: loggedInUnitId === 0 ? 1 : loggedInUnitId,
      label: "",
      unitTypeName: loggedInUnitId === 0 ? "Markaz" : "",
    },
    serialNumber: 0,
    birthDate: null,
    cnic: "",
    fatherName: "",
    fullName: "",
    joiningDate: null,
    referenceNote: "",
    docs: "",
    proceedRequest: false,
    isRegisteredAsHabib: true,
    id: "",
    fullNameEn: "",
    unitId: "",
  };

  // #endregion DataFunctionality

  // #region declareStates
  const { t } = useTranslation();
  const [buttonDisable, setButtonDisable] = useState(false);
  const { errors, setErrors, values, setValues } = useForm(memberValues);
  const [selectedDateOfBirth, setSelectedDateOfBirth] = useState(null);
  const [selectedDateOfJoining, setSelectedDateOfJoining] = useState(null);

  const [valuesFiles, setValuesFiles] = useState({
    baitFormFront: "",
    baitFormBack: "",
    kawaifForm: "",
  });
  const [errorFiles, setErrorFiles] = useState({
    baitFormFront: "",
    baitFormBack: "",
    kawaifForm: "",
  });

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);

  const notify = (string) => toast.success(string);
  const [similarRecords, setSimilarRecords] = useState({
    cnic: "",
    fullName: "",
    fatherName: "",
  });

  const [showSimilarRecords, setShowSimilarRecords] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [exactMatchRecords, setExactMatchRecords] = useState(null);
  const [similarMatchRecords, setSimilarMatchRecords] = useState(null);
  const [cnicMatchRecords, setCnicMatchRecords] = useState(null);
  const [hideUrduInput, setHideUrduInput] = useState(false);
  // #endregion declareStates

  // #region useEffects
  useEffect(() => {
    if (!member) return;

    const nameToUse = member?.habibName || member?.fullName || "";
    const isNameUrdu = isUrdu(nameToUse);

    // ✅ Build the values object
    const newValues = {
      Id: member.id,
      fullNameEn: nameToUse,
      birthDate: member?.dateofBirth || "",
      cnic: "",
      fatherName: member?.fatherName || "",
      fullName: "",
      joiningDate: "",
      referenceNote: member?.referenceNote || "",
      unit: {
        value: memberUnitId || loggedInUnitId,
        label: member?.unitName || "",
        unitTypeName: member?.unitTypeName || "",
      },
    };

    // ✅ Handle Urdu logic
    if (isNameUrdu) {
      newValues.fullName = nameToUse;
      newValues.fullNameEn = nameToUse;
      setHideUrduInput(true);
    } else {
      setHideUrduInput(false);
    }

    setValues(newValues);

    // ✅ Prepare similar record search payload
    setSimilarRecords({
      fullName: member?.habibName || member?.fullName || "",
      fatherName: "",
      cnic: "",
    });

    // ✅ Handle date of birth (convert to Date object)
    if (member?.dateofBirth) {
      const [day, month, year] = member.dateofBirth.split("/");
      const formattedDate = new Date(`${year}-${month}-${day}`);
      setSelectedDateOfBirth(formattedDate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, loggedInUnitId, memberUnitId]);

  useEffect(() => {
    if (!Object.values(similarRecords).every((x) => x === "")) {
      const timeoutId = setTimeout(() => {
        setShowSimilarRecords(true);
        setLoadingRecords(true);
        addMember()
          .GetMemberSimilarRecords(similarRecords)
          .then((res) => {
            setCnicMatchRecords(res.data.cnicRecords);
            setExactMatchRecords(res.data.exactMatchRecords);
            setSimilarMatchRecords(res.data.similarRecords);
            setLoadingRecords(false);
            if (
              Array.isArray(res.data.exactMatchRecords) &&
              res.data.exactMatchRecords.length === 0 &&
              Array.isArray(res.data.similarRecords) &&
              res.data.similarRecords.length === 0 &&
              (res.data.cnicRecords === null ||
                (Array.isArray(res.data.cnicRecords) &&
                  res.data.cnicRecords.length === 0))
            ) {
              setShowSimilarRecords(false);
            }
          })
          .catch((err) => {
            setLoadingRecords(false);
          });
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [similarRecords]);

  // #endregion useEffects

  // #region functions

  const validate = () => {
    let temp = {};
    // Basic text validations
    temp.fullName = values.fullName?.trim() ? (
      ""
    ) : (
      <GetLanguageString props="member_first_name_error_message" />
    );
    temp.fatherName = values.fatherName?.trim() ? (
      ""
    ) : (
      <GetLanguageString props="member_father_name_error_message" />
    );

    temp.cnic = values.cnic?.trim() ? (
      values.cnic.length === 13 ? (
        ""
      ) : (
        "CNIC must be 13 digits."
      )
    ) : (
      <GetLanguageString props="member_cnic_error_message" />
    );

    temp.birthDate = selectedDateOfBirth ? (
      DateOfBirthValidater(selectedDateOfBirth) ? (
        ""
      ) : (
        <GetLanguageString props="member_birth_date_less_error_message" />
      )
    ) : (
      <GetLanguageString props="member_birth_date_error_message" />
    );

    temp.joiningDate = selectedDateOfJoining ? (
      JoiningDateValidater(selectedDateOfJoining) ? (
        ""
      ) : (
        <GetLanguageString props="member_joining_date_range_error_message" />
      )
    ) : (
      <GetLanguageString props="member_joining_date_error_message" />
    );

    // Unit validation
    temp.unitError =
      values.unit.unitTypeName !== "Markaz" &&
      values.unit.unitTypeName !== "Zone" &&
      values.unit.unitTypeName !== "ZoneOS" ? (
        ""
      ) : (
        <GetLanguageString props="member_unit_check_error" />
      );

    // File validations
    if (CheckPermission("CanRequestAddMember")) {
      temp.baitFormFront = valuesFiles.baitFormFront ? (
        ""
      ) : (
        <GetLanguageString props="member_add_file_not_uploaded" />
      );

      temp.baitFormBack = valuesFiles.baitFormBack ? (
        ""
      ) : (
        <GetLanguageString props="member_add_file_not_uploaded" />
      );
    }

    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    if (CheckPermission("CanRequestAddMember")) {
      if (
        name === "fullName" ||
        name === "fatherName" ||
        name === "fullNameEn"
      ) {
        setValues({
          ...values,
          [name]: value,
        });
        setSimilarRecords({
          ...similarRecords,
          [name]: value,
        });
      }
    }
    if (name === "fullName" || name === "fatherName" || name === "fullNameEn") {
      setSimilarRecords({
        ...similarRecords,
        [name]: value,
      });
    }
    if (name === "cnic") {
      setValues({
        ...values,
        cnic: value.replace(/-/g, ""),
      });
      setSimilarRecords({
        ...similarRecords,
        cnic: value.replace(/-/g, ""),
      });
    }
    if (name === "cnic") {
      setSimilarRecords({
        ...similarRecords,
        cnic: value.replace(/-/g, ""),
      });
    }
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  const handleUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size < 10 * 1024 * 1024) {
      const options = {
        maxSizeMB: 10,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };

      let compressedFile;
      if (!selectedFile.type.includes("pdf")) {
        compressedFile = await imageCompression(selectedFile, options);
      } else {
        compressedFile = selectedFile;
      }

      if (e.target.name === "baitFormFront") {
        setValuesFiles({
          ...valuesFiles,
          baitFormFront: compressedFile,
        });
        setErrorFiles({
          ...errorFiles,
          baitFormFront: "",
        });
        setErrors((prev) => ({ ...prev, baitFormFront: "" })); // ✅ clear validation error
      } else if (e.target.name === "baitFormBack") {
        setValuesFiles({
          ...valuesFiles,
          baitFormBack: compressedFile,
        });
        setErrorFiles({
          ...errorFiles,
          baitFormBack: "",
        });
        setErrors((prev) => ({ ...prev, baitFormBack: "" })); // ✅ clear validation error
      } else if (e.target.name === "kawaifForm") {
        setValuesFiles({
          ...valuesFiles,
          kawaifForm: compressedFile,
        });
        setErrorFiles({
          ...errorFiles,
          kawaifForm: "",
        });
        setErrors((prev) => ({ ...prev, kawaifForm: "" })); // ✅ clear validation error
      }
    } else {
      if (e.target.name === "baitFormFront") {
        setValuesFiles({
          ...valuesFiles,
          baitFormFront: null,
        });
        setErrorFiles({
          ...errorFiles,
          baitFormFront:
            selectedFile?.size > 10 * 1024 * 1024 ? (
              <GetLanguageString props="member_add_file_upload_size_error" />
            ) : (
              <GetLanguageString props="member_add_file_upload_error" />
            ),
        });
      } else if (e.target.name === "baitFormBack") {
        setValuesFiles({
          ...valuesFiles,
          baitFormBack: null,
        });
        setErrorFiles({
          ...errorFiles,
          baitFormBack:
            selectedFile?.size > 10 * 1024 * 1024 ? (
              <GetLanguageString props="member_add_file_upload_size_error" />
            ) : (
              <GetLanguageString props="member_add_file_upload_error" />
            ),
        });
      } else if (e.target.name === "kawaifForm") {
        setValuesFiles({
          ...valuesFiles,
          kawaifForm: null,
        });
        setErrorFiles({
          ...errorFiles,
          kawaifForm:
            selectedFile?.size > 10 * 1024 * 1024 ? (
              <GetLanguageString props="member_add_file_upload_size_error" />
            ) : (
              <GetLanguageString props="member_add_file_upload_error" />
            ),
        });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Prepare values before validation
    if (!values.isRegisteredAsHabib) {
      values.isRegisteredAsHabib = true;
    }
    if (hideUrduInput) {
      values.fullName = values.fullNameEn;
    }
    values.birthDate = convertToDateString(selectedDateOfBirth);
    values.joiningDate = convertToDateString(selectedDateOfJoining);

    // Validate
    const isValid = validate();
    if (!isValid) {
      setButtonDisable(false);
      setLoading(false);
      return;
    }

    // Call the actual submission logic
    submitMember();
  };

  // Separate function for the actual submission
  const submitMember = () => {
    setLoading(true);
    setButtonDisable(true);

    const files = new FormData();
    files.append("BaitFormFront", valuesFiles.baitFormFront);
    files.append("BaitFormBack", valuesFiles.baitFormBack);
    files.append("KawaifForm", valuesFiles.kawaifForm);

    ApiNotifications()
      .NotificationDocsSave(files)
      .then((res) => {
        values.docs = res.data;
        addMember()
          .setMember(values)
          .then((res) => {
            if (
              res.data.isRequest &&
              res.data.message === "request_exists_for_rafiq"
            ) {
              swal({
                title: t("request_exists_for_rafiq"),
                className: GetSelectedLanguage() === "en" ? "" : "ur",
                icon: "error",
                buttons: GetSelectedLanguage() === "en" ? "OK" : "ٹھیک ہے ",
              });
              setLoading(false);
              setButtonDisable(false);
            } else if (
              res.data.isRequest &&
              res.data.proceedRequest &&
              res.data.message === "request_exists_for_rafiq_proceed_or_not"
            ) {
              setLoading(false);
              setButtonDisable(false);
              swal({
                title: t("request_exists_for_rafiq_proceed_or_not"),
                className: GetSelectedLanguage() === "en" ? "" : "ur",
                icon: "error",
                buttons: {
                  cancel: true,
                  confirm: t("request_proceed_anyway"),
                },
              }).then((proceed) => {
                if (proceed) {
                  values.proceedRequest = true;
                  submitMember(); // ✅ call submission again safely
                }
              });
            } else if (
              res.data.isRequest &&
              res.data.message === "Request Sent"
            ) {
              notify(t("request_to_add_rafiq"));
              setLoading(false);
              setHabibMemberModal(false);
              setButtonDisable(false);
            }
          })
          .catch((err) => {
            const messageMatch =
              err.response.data.match(/Message\s*=\s*(.*?),/);
            const errorMessage = messageMatch
              ? messageMatch[1]
              : "Error occurred";
            setButtonDisable(false);
            swal({
              title: errorMessage,
              icon: "error",
              buttons: "OK",
            });
          });
      });
  };

  // #endregion functions

  return (
    <>
      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal
          isOpen={true}
          size="lg"
          style={{ maxWidth: "1080px", width: "100%" }}
          className="inmodal"
        >
          <ModalHeader className="modal-title" tag="h4">
            {<GetLanguageString props="convert_habib_to_member" />}
          </ModalHeader>
          <Form onSubmit={handleSubmit}>
            {!loading ? (
              <ModalBody>
                <div className="ibox ">
                  <div className="ibox-content">
                    <Row>
                      <Col md={hideUrduInput ? "6" : "4"}>
                        <FormGroup>
                          <Label className="col-form-label">
                            <GetLanguageString props="member_first_name" />
                          </Label>
                          <Input
                            className="ur no-drag"
                            autoFocus
                            type="text"
                            name="fullNameEn"
                            onChange={handleInputChange}
                            value={values.fullNameEn}
                            // {...(errors.fullNameEn && { invalid: true })}
                          />
                          {/* {errors.fullNameEn && (
        <FormFeedback>
          <h4>{errors.fullNameEn}</h4>
        </FormFeedback>
      )} */}
                        </FormGroup>
                      </Col>

                      {!hideUrduInput && (
                        <Col md="4">
                          <FormGroup>
                            <Label className="col-form-label">
                              <GetLanguageString props="urdu_name" />
                            </Label>
                            <Input
                              className="ur no-drag"
                              autoFocus
                              type="text"
                              onChange={handleInputChange}
                              name="fullName"
                              {...(errors.fullName && { invalid: true })}
                            />
                            {errors.fullName && (
                              <FormFeedback>
                                <h4>{errors.fullName}</h4>
                              </FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                      )}

                      <Col md={hideUrduInput ? "6" : "4"}>
                        <FormGroup>
                          <Label className="col-form-label">
                            <GetLanguageString props="member_father_name" />
                          </Label>
                          <Input
                            className="ur no-drag"
                            autoFocus
                            type="text"
                            value={values.fatherName}
                            onChange={handleInputChange}
                            name="fatherName"
                            {...(errors.fatherName && { invalid: true })}
                          />
                          {errors.fatherName && (
                            <FormFeedback>
                              <h4>{errors.fatherName}</h4>
                            </FormFeedback>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label className="col-form-label">
                            {<GetLanguageString props="member_joining_date" />}
                          </Label>
                          <DatePicker
                            showYearDropdown
                            dropdownMode="select"
                            placeholderText="dd/mm/yyyy"
                            className={`form-control no-drag ${
                              errors.joiningDate ? "is-invalid" : ""
                            }`}
                            selected={selectedDateOfJoining}
                            maxDate={new Date()}
                            onChange={(date) => {
                              setSelectedDateOfJoining(date);
                              if (date) {
                                setErrors((prev) => ({
                                  ...prev,
                                  joiningDate: "",
                                }));
                              }
                            }}
                            dateFormat="dd/MM/yyyy"
                          />
                          {errors.joiningDate && (
                            <div className="invalid-feedback d-block">
                              <h4>{errors.joiningDate}</h4>
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label className="col-form-label">
                            {<GetLanguageString props="member_cnic" />}
                          </Label>
                          <InputMask
                            mask="99999-9999999-9"
                            onChange={handleInputChange}
                            name="cnic"
                            maskChar=""
                          >
                            {(inputProps) => (
                              <Input
                                {...inputProps}
                                type="text"
                                className="no-drag"
                                placeholder="______-_______-_"
                                {...(errors.cnic && { invalid: true })}
                              />
                            )}
                          </InputMask>
                          {errors.cnic && (
                            <FormFeedback>
                              <h4>{errors.cnic}</h4>
                            </FormFeedback>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label className="col-form-label">
                            {<GetLanguageString props="member_date_of_birth" />}
                          </Label>
                          <DatePicker
                            showYearDropdown
                            dropdownMode="select"
                            placeholderText="dd/mm/yyyy"
                            className={`form-control no-drag ${
                              errors.birthDate ? "is-invalid" : ""
                            }`}
                            selected={selectedDateOfBirth}
                            maxDate={new Date()}
                            onChange={(date) => {
                              setSelectedDateOfBirth(date);
                              if (date) {
                                setErrors((prev) => ({
                                  ...prev,
                                  birthDate: "",
                                }));
                              }
                            }}
                            dateFormat="dd/MM/yyyy"
                          />
                          {errors.birthDate && (
                            <div className="invalid-feedback d-block">
                              <h4>{errors.birthDate}</h4>
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label className="col-form-label">
                            {
                              <GetLanguageString props="member_reference_note" />
                            }
                          </Label>
                          <Input
                            className="ur no-drag"
                            autoFocus
                            type="text"
                            onChange={handleInputChange}
                            name="refrenceNote"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    {CheckPermission("CanRequestAddMember") ? (
                      <>
                        <Row>
                          <Col md="6">
                            <Label className="col-form-label">
                              {
                                <GetLanguageString props="member_add_bait_form_front" />
                              }
                            </Label>
                            <Input
                              type="file"
                              accept="image/*"
                              name="baitFormFront"
                              maxSize={10 * 1024 * 1024}
                              onChange={handleUpload}
                            />
                            {errorFiles && (
                              <div className="text-error">
                                {errorFiles.baitFormFront}
                              </div>
                            )}
                            {errors.baitFormFront && (
                              <h4 className="text-error">
                                {errors.baitFormFront}
                              </h4>
                            )}
                          </Col>
                          <Col md="6">
                            <Label className="col-form-label">
                              {
                                <GetLanguageString props="member_add_bait_form_back" />
                              }
                            </Label>
                            <Input
                              type="file"
                              accept="image/*"
                              name="baitFormBack"
                              maxSize={10 * 1024 * 1024}
                              onChange={handleUpload}
                            />
                            {errorFiles && (
                              <div className="text-error">
                                {errorFiles.baitFormBack}
                              </div>
                            )}
                            {errors.baitFormBack && (
                              <h4 className="text-error">
                                {errors.baitFormBack}
                              </h4>
                            )}
                          </Col>
                          <Col md="6">
                            <Label className="col-form-label">
                              {
                                <GetLanguageString props="member_add_kawaif_form" />
                              }
                            </Label>
                            <Input
                              type="file"
                              accept="image/*,application/pdf"
                              name="kawaifForm"
                              maxSize={10 * 1024 * 1024}
                              onChange={handleUpload}
                            />

                            {errorFiles.kawaifForm && (
                              <div className="text-error">
                                {errorFiles.kawaifForm}
                              </div>
                            )}
                          </Col>
                        </Row>
                        <br></br>
                      </>
                    ) : null}
                    <Row>
                      <SelectMemberUnit
                        values={values}
                        setValues={setValues}
                        unitId={member.unitId}
                        showExcludeUnit={false}
                      />
                      {errors.unitError && (
                        <h4 className="text-error">{errors.unitError}</h4>
                      )}
                    </Row>
                  </div>
                  <br></br>
                  {CheckPermission("CanRequestAddMember") ? (
                    showSimilarRecords ? (
                      !loadingRecords ? (
                        <>
                          <div className="ibox">
                            <div className="ibox-title">
                              <h5>
                                {
                                  <GetLanguageString props="notifications_see_changes_similar_records_exact" />
                                }
                              </h5>
                            </div>
                            <div
                              className="ibox-content no-drag"
                              style={{
                                maxHeight: 300,
                                overflowY: "auto",
                              }}
                            >
                              <div>
                                {exactMatchRecords?.length !== 0 ? (
                                  <TableView data={exactMatchRecords} />
                                ) : (
                                  <NoResult />
                                )}
                              </div>
                            </div>
                          </div>

                          <br></br>
                          <div className="ibox">
                            <div className="ibox-title">
                              <h5>
                                {
                                  <GetLanguageString props="notifications_see_changes_similar_records_similar" />
                                }
                              </h5>
                            </div>
                            <div
                              className="ibox-content no-drag"
                              style={{
                                maxHeight: 300,
                                overflowY: "auto",
                              }}
                            >
                              <div>
                                {similarMatchRecords?.length !== 0 ? (
                                  <TableView data={similarMatchRecords} />
                                ) : (
                                  <NoResult />
                                )}
                              </div>
                            </div>
                          </div>
                          <br></br>
                          <div className="ibox">
                            <div className="ibox-title">
                              <h5>
                                {
                                  <GetLanguageString props="notifications_see_changes_similar_records_cnic" />
                                }
                              </h5>
                            </div>
                            <div
                              className="ibox-content no-drag"
                              style={{
                                maxHeight: 300,
                                overflowY: "auto",
                              }}
                            >
                              <div>
                                {cnicMatchRecords?.length !== 0 &&
                                cnicMatchRecords !== null ? (
                                  <TableView data={cnicMatchRecords} />
                                ) : (
                                  <NoResult />
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        LoadingSpinner()
                      )
                    ) : null
                  ) : null}
                </div>
              </ModalBody>
            ) : (
              LoadingSpinner()
            )}

            <ModalFooter>
              <Button
                color="default"
                className="no-drag"
                onClick={() => {
                  setHabibMemberModal(false);
                }}
              >
                {<GetLanguageString props="common_cancel" />}
              </Button>
              <Button
                color="primary"
                className="no-drag"
                disabled={buttonDisable}
                type="submit"
              >
                {<GetLanguageString props="common_save_changes" />}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </Draggable>
    </>
  );
};

export default ConvertHabibToRafiq;
